import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const cleaningAxios = axios.create({
  baseURL: API,
  headers: { 'Content-Type': 'application/json' },
});

cleaningAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem('cleaning_admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});
cleaningAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      localStorage.removeItem('cleaning_admin_token');
      if (window.location.pathname !== '/cleaning/admin') {
        window.location.href = '/cleaning/admin';
      }
    }
    return Promise.reject(error);
  }
);

export const cleaningApi = {
  // Services
  getServices: () => cleaningAxios.get('/cleaning/services'),
  
  // Bookings
  createBooking: (data) => cleaningAxios.post('/cleaning/bookings', data),
  createCheckout: (bookingId, originUrl) => cleaningAxios.post('/cleaning/payments/checkout', { booking_id: bookingId, origin_url: originUrl }),
  
  // Admin
  adminLogin: (email, password) => cleaningAxios.post('/cleaning/admin/login', { email, password }),
  forgotPassword: (email) => cleaningAxios.post('/cleaning/admin/forgot-password', { email }),
  getBookings: () => cleaningAxios.get('/cleaning/admin/bookings'),
  getStats: () => cleaningAxios.get('/cleaning/admin/stats'),
  updateBookingStatus: (bookingId, status) => cleaningAxios.patch(`/cleaning/admin/bookings/${bookingId}`, { status }),
  updateBooking: (bookingId, data) => cleaningAxios.patch(`/cleaning/admin/bookings/${bookingId}`, data),
  emailCustomer: (bookingId, subject, message) => cleaningAxios.post(`/cleaning/admin/bookings/${bookingId}/email-customer`, { subject, message }),
  createAdminBooking: (data) => cleaningAxios.post('/cleaning/admin/bookings/create', data),
  
  // Trash (soft delete)
  deleteBooking: (bookingId) => cleaningAxios.delete(`/cleaning/admin/bookings/${bookingId}`),
  getTrashedBookings: () => cleaningAxios.get('/cleaning/admin/bookings/trash'),
  restoreBooking: (bookingId) => cleaningAxios.post(`/cleaning/admin/bookings/${bookingId}/restore`),
  permanentDeleteBooking: (bookingId) => cleaningAxios.delete(`/cleaning/admin/bookings/${bookingId}/permanent`),
  
  // Emails
  sendEmail: (recipientEmail, subject, htmlContent, files = []) => {
    const formData = new FormData();
    formData.append('recipient_email', recipientEmail);
    formData.append('subject', subject);
    formData.append('html_content', htmlContent);
    files.forEach(f => formData.append('files', f));
    return cleaningAxios.post('/cleaning/emails/send', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getSentEmails: () => cleaningAxios.get('/cleaning/admin/emails/sent'),
  getReceivedEmails: () => cleaningAxios.get('/cleaning/admin/emails/received'),
  markEmailRead: (emailId) => cleaningAxios.patch(`/cleaning/admin/emails/received/${emailId}/read`),
  markEmailUnread: (emailId) => cleaningAxios.patch(`/cleaning/admin/emails/received/${emailId}/unread`),
  toggleStarReceived: (emailId) => cleaningAxios.patch(`/cleaning/admin/emails/received/${emailId}/star`),
  toggleStarSent: (emailId) => cleaningAxios.patch(`/cleaning/admin/emails/sent/${emailId}/star`),
  deleteReceivedEmail: (emailId) => cleaningAxios.delete(`/cleaning/admin/emails/received/${emailId}`),
  deleteSentEmail: (emailId) => cleaningAxios.delete(`/cleaning/admin/emails/sent/${emailId}`),
  bulkDeleteEmails: (emailIds, type) => cleaningAxios.post('/cleaning/admin/emails/bulk-delete', { email_ids: emailIds, type }),
  bulkMarkRead: (emailIds) => cleaningAxios.post('/cleaning/admin/emails/bulk-read', { email_ids: emailIds }),
  retryAttachments: (emailId) => cleaningAxios.post(`/cleaning/admin/emails/received/${emailId}/retry-attachments`),
  
  // Email Trash
  getTrashedEmails: () => cleaningAxios.get('/cleaning/admin/emails/trash'),
  restoreEmail: (emailId, type) => cleaningAxios.post(`/cleaning/admin/emails/${emailId}/restore`, { type }),
  permanentDeleteEmail: (emailId, type) => cleaningAxios.delete(`/cleaning/admin/emails/${emailId}/permanent?type=${type}`),
  
  // Analytics
  getSalesAnalytics: () => cleaningAxios.get('/cleaning/admin/analytics/sales'),
  getTrafficAnalytics: () => cleaningAxios.get('/cleaning/admin/analytics/traffic'),
  
  // Checklist
  updateChecklist: (bookingId, checklist) => cleaningAxios.patch(`/cleaning/admin/bookings/${bookingId}/checklist`, { checklist }),
  
  // Quick Reply Templates
  getQuickReplies: () => cleaningAxios.get('/cleaning/admin/quick-replies'),
  createQuickReply: (data) => cleaningAxios.post('/cleaning/admin/quick-replies', data),
  updateQuickReply: (id, data) => cleaningAxios.patch(`/cleaning/admin/quick-replies/${id}`, data),
  deleteQuickReply: (id) => cleaningAxios.delete(`/cleaning/admin/quick-replies/${id}`),
  
  // Conflict-aware restore
  checkRestoreConflict: (bookingId) => cleaningAxios.get(`/cleaning/admin/bookings/${bookingId}/check-conflict`),
  restoreBookingWithNewTime: (bookingId, newDate, newTime) => cleaningAxios.post(`/cleaning/admin/bookings/${bookingId}/restore-reschedule`, { new_date: newDate, new_time: newTime }),
  
  // Geocoding for map
  geocodeAddress: (address) => cleaningAxios.post('/cleaning/admin/geocode', { address }),

  // Availability (public)
  getAvailability: (date, numVehicles = 1, isAdmin = false, serviceType = 'residential', zipCode = '', street = '', city = '', lat = '', lng = '', serviceCategory = 'residential') =>
    cleaningAxios.get(`/cleaning/availability?date=${date}&num_vehicles=${numVehicles}&is_admin=${isAdmin}&service_type=${serviceType}&service_category=${serviceCategory}&zip_code=${zipCode}&street=${street}&city=${city}&lat=${lat}&lng=${lng}`),
  // Check Zone
  checkZone: (street, city, zipCode, lat, lng) => {
    let url = `/cleaning/check-zone?street=${encodeURIComponent(street)}&city=${encodeURIComponent(city)}&zip_code=${zipCode}`;
    if (lat != null && lng != null) {
      url += `&lat=${lat}&lng=${lng}`;
    }
    return cleaningAxios.get(url);
  },


  // Time blocks (admin)
  getTimeBlocks: (date) => cleaningAxios.get(`/cleaning/admin/time-blocks${date ? `?date=${date}` : ''}`),
  createTimeBlock: (data) => cleaningAxios.post('/cleaning/admin/time-blocks', data),
  deleteTimeBlock: (blockId) => cleaningAxios.delete(`/cleaning/admin/time-blocks/${blockId}`),

  // Crew assignment
  assignCrew: (bookingId, crewAssignment) =>
    cleaningAxios.patch(`/cleaning/admin/bookings/${bookingId}/crew`, { crew_assignment: crewAssignment }),

  // Day schedule (admin)
  getDaySchedule: (date) => cleaningAxios.get(`/cleaning/admin/day-schedule?date=${date}`),
  // Export
  exportBookings: () => cleaningAxios.get('/cleaning/admin/bookings/export', { responseType: 'blob' }),
  
  // Admin Calendar
  getAdminCalendarAvailability: (start, end, serviceType) => cleaningAxios.get(`/cleaning/admin/calendar-availability?start_date=${start}&end_date=${end}&service_type=${serviceType}`),
};
